import { useRef, useEffect, useState } from 'react'
import { motion, useInView, useScroll, useTransform } from 'framer-motion'
import { Users, Smile, TrendingUp } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

const stats = [
  { value: 20, suffix: '+', label: 'Years of Combined Experience', icon: Users },
  { value: 60, suffix: '+', label: 'Happy Clients', icon: Smile },
  { value: 30, suffix: '+ Cr.', label: 'Revenue Generated for Clients', prefix: '₹', icon: TrendingUp },
]

function useCountUp(end: number, duration: number, start: boolean) {
  const [count, setCount] = useState(0)
  useEffect(() => {
    if (!start) return
    let startTime: number
    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp
      const elapsed = timestamp - startTime
      const progress = Math.min(elapsed / duration, 1)
      setCount(Math.floor(progress * end))
      if (progress < 1) requestAnimationFrame(step)
    }
    requestAnimationFrame(step)
  }, [end, duration, start])
  return count
}

function StatBlock({
  value,
  prefix = '',
  suffix,
  label,
  icon: Icon,
  delay,
}: {
  value: number
  prefix?: string
  suffix: string
  label: string
  icon: LucideIcon
  delay: number
}) {
  const ref = useRef<HTMLDivElement>(null)
  const inView = useInView(ref, { once: true })
  const count = useCountUp(value, 1500, inView)

  return (
    <motion.div
      ref={ref}
      className="card-glass text-center p-6 sm:p-8 group relative overflow-hidden"
      initial={{ opacity: 0, scale: 0.92 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true }}
      transition={{ delay, duration: 0.5, type: 'spring', stiffness: 120 }}
      whileHover={{ y: -4 }}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-accent/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      <div className="relative z-10 flex flex-col items-center">
        <div className="w-12 h-12 rounded-2xl bg-primary/10 dark:bg-accent/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
          <Icon className="text-primary dark:text-accent" size={24} strokeWidth={1.8} />
        </div>
        <div className="text-2xl sm:text-3xl lg:text-4xl font-bold text-primary dark:text-accent">
          {prefix}{count}{suffix}
        </div>
        <div className="mt-2 text-slate-600 dark:text-slate-400 font-medium text-xs sm:text-sm">{label}</div>
      </div>
    </motion.div>
  )
}

export function OurImpact() {
  const containerRef = useRef<HTMLDivElement>(null)
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start end', 'end start']
  })
  
  const y = useTransform(scrollYProgress, [0, 1], [60, -60])

  return (
    <section ref={containerRef} className="section-pad px-4 sm:px-6 lg:px-8 bg-white dark:bg-navy-900 relative overflow-hidden">
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[400px] h-[400px] light-orb light-orb-accent opacity-15 dark:opacity-0 pointer-events-none" aria-hidden />
      
      <motion.div 
        style={{ y }}
        className="absolute -right-10 top-10 w-48 h-48 lg:w-72 lg:h-72 pointer-events-none hidden md:flex items-center justify-center"
      >
        <div className="absolute inset-0 bg-accent/10 dark:bg-accent/5 rounded-full blur-3xl" />
        <div className="absolute w-full h-full border-2 border-dashed border-accent/30 rounded-full animate-[spin_20s_linear_infinite]" />
        <div className="absolute w-2/3 h-2/3 border-2 border-primary/30 rounded-full animate-[spin_15s_linear_infinite_reverse]" />
        <div className="absolute w-1/3 h-1/3 bg-gradient-to-tr from-primary to-accent rounded-full opacity-50 blur-md animate-pulse" />
      </motion.div>

      <div className="container-wide relative z-10">
        <div className="text-center mb-10 sm:mb-14 max-w-3xl mx-auto">
          <motion.h2
            className="section-heading mb-4"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Our Impact
          </motion.h2>
          <motion.p
            className="section-sub"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
          >
            We don't just run campaigns; we build scalable systems that deliver measurable business outcomes. Here is a glimpse of what we have achieved for our partners.
          </motion.p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 max-w-4xl mx-auto">
          {stats.map((s, i) => (
            <StatBlock
              key={s.label}
              value={s.value}
              prefix={s.prefix}
              suffix={s.suffix}
              label={s.label}
              icon={s.icon}
              delay={i * 0.08}
            />
          ))}
        </div>
      </div>
    </section>
  )
}
